import { Button } from "react-daisyui";
import { useRouteError } from "react-router-dom";
import { ErrorScreen } from "./ErrorScreen";
import { useLatestVersion } from "src/core/service-worker/useLatestVersion";
import { isLaterVersion } from "src/core/service-worker/compareVersions";
import { releaseVersion } from "src/core/service-worker/releaseVersion";

/**
 * Screens are loaded in pieces as they are opened, and only the startup pieces are precached. A
 * piece fails to load either because the tab is from an old release that Hosting no longer
 * serves, or because the connection dropped. Reloading fixes both.
 */
const isScreenLoadError = (error: unknown) =>
    error instanceof Error &&
    /dynamically imported module|Importing a module script failed|ChunkLoadError/i.test(
        `${error.name} ${error.message}`
    );

interface Props {
    title?: string;
    message?: string;
}

export const RouteErrorScreen = ({ title, message }: Props) => {
    const error = useRouteError();
    const { data: latestVersion } = useLatestVersion();

    // the API answering with a later release is the only sign the tab is stale rather than offline
    if (isScreenLoadError(error) && isLaterVersion(latestVersion, releaseVersion)) {
        return (
            <ErrorScreen
                className="min-h-screen"
                title="Hub has been updated"
                message="This screen belongs to a newer version of Hub. Reload to carry on."
                action={
                    <Button type="button" color="primary" onClick={() => window.location.reload()}>
                        Reload Hub
                    </Button>
                }
            />
        );
    }

    if (isScreenLoadError(error)) {
        return (
            <ErrorScreen
                className="min-h-screen"
                title="This screen didn't open"
                message="There was an error opening this screen. Try again, or contact the church for help."
                action={
                    <Button type="button" color="primary" onClick={() => window.location.reload()}>
                        Try again
                    </Button>
                }
            />
        );
    }

    return (
        <ErrorScreen
            className="min-h-screen"
            title={title ?? "Something went wrong"}
            message={message ?? "Something happened that we couldn't recover from."}
            errorObj={error instanceof Error ? error : undefined}
            action={
                <Button type="button" color="primary" onClick={() => window.location.reload()}>
                    Reload Hub
                </Button>
            }
        />
    );
};
